/**
 * Client-side audio helpers.
 *
 * Shares a single AudioContext with state/audio.ts (via getAudioContext).
 * Two caches live here:
 *   - decodedCache    keyed by URL → AudioBuffer, so tapping a letter
 *                     twice does not re-fetch or re-decode.
 *   - inflight        deduplicates parallel prefetches.
 *
 * Two kinds of letter audio live side-by-side in R2:
 *   - /assets/audio/letters/{id}_isolated.mp3   → the SOUND ("أَ" for ا)
 *   - /assets/audio/letters/{id}_name.mp3       → the NAME ("أَلِف" for ا)
 *
 * The name is what a parent expects to hear when first meeting the letter.
 * The sound is the target of the Hear and Say stages. Pick intentionally.
 */

import { unlockAudio, audioUnlocked, getAudioContext } from '../state/audio';

/** Re-exported so callers can show "tap to start" hints tied to the signal. */
export { unlockAudio, audioUnlocked };

/** URL for the letter NAME audio (e.g. "أَلِف" for Alif). */
export function letterNameUrl(letterId: number): string {
  return `/assets/audio/letters/${letterId}_name.mp3`;
}

/** URL for the letter SOUND audio (e.g. "أَ" fatha for Alif). */
export function letterSoundUrl(letterId: number): string {
  return `/assets/audio/letters/${letterId}_isolated.mp3`;
}

/** Cached decoded AudioBuffers, keyed by URL. Lives for the session. */
const decodedCache = new Map<string, AudioBuffer>();

/** In-flight fetches so parallel prefetch calls don't duplicate work. */
const inflight = new Map<string, Promise<AudioBuffer | null>>();

async function loadAndDecode(url: string): Promise<AudioBuffer | null> {
  const cached = decodedCache.get(url);
  if (cached) return cached;
  const existing = inflight.get(url);
  if (existing) return existing;

  const ac = getAudioContext();
  if (!ac) return null;

  const p = (async () => {
    try {
      const res = await fetch(url);
      if (!res.ok) return null;
      const bytes = await res.arrayBuffer();
      const buffer = await ac.decodeAudioData(bytes);
      decodedCache.set(url, buffer);
      return buffer;
    } catch (err) {
      console.warn('[huroof] audio decode failed for', url, err);
      return null;
    } finally {
      inflight.delete(url);
    }
  })();
  inflight.set(url, p);
  return p;
}

export async function playUrl(url: string): Promise<void> {
  const ctx = getAudioContext();
  if (!ctx || !audioUnlocked.value) return;
  if (ctx.state === 'suspended') {
    try {
      await ctx.resume();
    } catch {
      // If resume fails silently, the play call below will just be a no-op.
    }
  }
  const buffer = await loadAndDecode(url);
  if (!buffer) return;
  try {
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(ctx.destination);
    source.start(0);
  } catch (err) {
    console.warn('[huroof] playUrl failed:', err);
  }
}

/**
 * Preload a set of audio URLs into the decoded-buffer cache so the next
 * playUrl() plays with zero delay. Fire-and-forget; errors are swallowed.
 * If the AudioContext isn't yet unlocked, still fetch the bytes into the
 * HTTP cache so the subsequent fetch is instant.
 */
export function prefetchAudio(urls: string[]): void {
  const ctx = getAudioContext();
  for (const url of urls) {
    if (ctx && audioUnlocked.value) {
      void loadAndDecode(url);
    } else {
      // No AudioContext yet — still warm the HTTP cache. The SW will
      // cache-first match this on next request.
      void fetch(url, { cache: 'force-cache' }).catch(() => undefined);
    }
  }
}
